import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Flame,
  ChevronLeft,
  MapPin,
  Mail,
  Phone,
  Clock,
  Send,
  Loader2,
  Check,
  Instagram,
  Facebook,
  Youtube,
  Navigation,
  Car,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import Footer from '../components/Footer';
import { siteData } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDRESS = '370 Tomkins Court, Gilroy, CA 95020';
const MAP_EMBED_URL =
  'https://www.google.com/maps?q=' +
  encodeURIComponent(ADDRESS) +
  '&output=embed';
const DIRECTIONS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=' +
  encodeURIComponent(ADDRESS);

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { footer } = siteData;
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast({
        title: 'Missing info',
        description: 'Please fill in name, email, subject, and message.',
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/messages`, form);
      setSubmitted(true);
      toast({
        title: `Thanks, ${form.name.split(' ')[0]}!`,
        description: "We've received your message and will get back to you soon.",
      });
    } catch (err) {
      toast({
        title: 'Message failed to send',
        description: 'Please try again in a moment or email us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      {/* Slim brand bar */}
      <header className="bg-[#0a1030] text-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px] md:text-[15px]">
              APOSTOLIC CHRIST TABERNACLE
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Back to home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0a1030] via-[#111a4a] to-[#0a1030] text-white">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top,rgba(200,185,138,0.35),transparent_60%)]" />
        <div className="relative max-w-[1000px] mx-auto text-center px-6 py-20 md:py-24">
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-5">
            Get In Touch
          </p>
          <h1
            className="text-[46px] md:text-[76px] lg:text-[88px] leading-[1.02]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
          >
            Contact Us
          </h1>
          <p className="mt-6 text-[17px] md:text-[19px] text-white/85 max-w-[620px] mx-auto leading-relaxed">
            Have a question, prayer request, or just want to say hello? We'd
            love to hear from you.
          </p>
        </div>
      </section>

      {/* Main content */}
      <section className="py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left: info cards */}
          <div className="lg:col-span-2 space-y-4">
            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl p-6 md:p-7 border border-black/5 shadow-sm hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] tracking-wider uppercase text-[#8a7a4a] font-semibold">
                    Visit Us
                  </div>
                  <div
                    className="mt-1 text-[19px] text-[#0a1030] leading-snug group-hover:underline"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                  >
                    370 Tomkins Court<br />Gilroy, CA 95020
                  </div>
                  <div className="mt-2 text-[13px] text-[#6b6459] flex items-center gap-1">
                    <Navigation className="h-3.5 w-3.5" /> Get directions
                  </div>
                </div>
              </div>
            </a>

            <a
              href={`mailto:${footer.email}`}
              className="block bg-white rounded-2xl p-6 md:p-7 border border-black/5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[12px] tracking-wider uppercase text-[#8a7a4a] font-semibold">
                    Email
                  </div>
                  <div
                    className="mt-1 text-[19px] text-[#0a1030] leading-snug"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                  >
                    {footer.email}
                  </div>
                </div>
              </div>
            </a>

            <a
              href={`tel:${footer.phone.replace(/[^0-9+]/g, '')}`}
              className="block bg-white rounded-2xl p-6 md:p-7 border border-black/5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[12px] tracking-wider uppercase text-[#8a7a4a] font-semibold">
                    Phone
                  </div>
                  <div
                    className="mt-1 text-[19px] text-[#0a1030] leading-snug"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                  >
                    {footer.phone}
                  </div>
                </div>
              </div>
            </a>

            <div className="bg-white rounded-2xl p-6 md:p-7 border border-black/5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[12px] tracking-wider uppercase text-[#8a7a4a] font-semibold">
                    Service Times
                  </div>
                  <ul className="mt-2 text-[15px] text-[#3a3a3a] space-y-1.5">
                    <li className="flex justify-between gap-4">
                      <span className="font-medium text-[#0a1030]">Sunday Worship</span>
                      <span className="text-[#6b6459]">10:00 AM</span>
                    </li>
                    <li className="flex justify-between gap-4">
                      <span className="font-medium text-[#0a1030]">Monday Night Prayer</span>
                      <span className="text-[#6b6459]">6:30 PM</span>
                    </li>
                    <li className="flex justify-between gap-4">
                      <span className="font-medium text-[#0a1030]">Midweek Service</span>
                      <span className="text-[#6b6459]">Wed · 7:00 PM</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <a
                href={footer.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="h-10 w-10 rounded-full bg-white border border-black/10 flex items-center justify-center text-[#0a1030] hover:bg-[#0a1030] hover:text-white transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href={footer.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="h-10 w-10 rounded-full bg-white border border-black/10 flex items-center justify-center text-[#0a1030] hover:bg-[#0a1030] hover:text-white transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={footer.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="h-10 w-10 rounded-full bg-white border border-black/10 flex items-center justify-center text-[#0a1030] hover:bg-[#0a1030] hover:text-white transition-colors"
              >
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Right: message form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-10 md:p-14 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#0a1030] text-white flex items-center justify-center mb-5">
                  <Check className="h-7 w-7" />
                </div>
                <h2
                  className="text-[32px] md:text-[42px] leading-tight text-[#0a1030]"
                  style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                >
                  Message received, {form.name.split(' ')[0]}!
                </h2>
                <p className="mt-3 text-[#4a4a4a] max-w-[440px] mx-auto">
                  Thanks for reaching out. Someone from our team will get back
                  to you shortly.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate('/')}
                    className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white px-8 h-12"
                  >
                    Back to home
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
                    }}
                    className="rounded-full border-[#0a1030] text-[#0a1030] hover:bg-[#0a1030]/5 px-8 h-12"
                  >
                    Send another
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 md:p-10"
              >
                <h2
                  className="text-[26px] md:text-[32px] text-[#0a1030] leading-tight"
                  style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                >
                  Send us a message
                </h2>
                <p className="text-sm text-[#6b6459] mt-1 mb-6">
                  We usually reply within one business day.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-[13px] font-semibold">
                      Full name<span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      placeholder="Jane Doe"
                      className="mt-1.5 h-11 bg-[#f8f4ee] border-black/10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[13px] font-semibold">
                      Email<span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="you@email.com"
                      className="mt-1.5 h-11 bg-[#f8f4ee] border-black/10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="phone" className="text-[13px] font-semibold">
                      Phone <span className="text-[#8a8478] font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      placeholder="408-555-0123"
                      className="mt-1.5 h-11 bg-[#f8f4ee] border-black/10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-[13px] font-semibold">
                      Subject<span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="subject"
                      value={form.subject}
                      onChange={(e) => update('subject', e.target.value)}
                      placeholder="Prayer request, general question…"
                      className="mt-1.5 h-11 bg-[#f8f4ee] border-black/10"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="message" className="text-[13px] font-semibold">
                    Message<span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    rows={6}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder="How can we serve you?"
                    className="mt-1.5 bg-[#f8f4ee] border-black/10 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white h-12 px-10 disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" /> Send message
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map + Directions */}
      <section className="px-4 md:px-6 pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-2">
              <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a7a4a] font-semibold mb-3">
                Find Us
              </p>
              <h2
                className="text-[34px] md:text-[44px] text-[#0a1030] leading-tight"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                Directions from the Freeway
              </h2>
              <p className="mt-4 text-[#4a4a4a] leading-relaxed">
                We're just off <span className="font-semibold text-[#0a1030]">US-101</span>
                {' '}in Gilroy — about a 3-minute drive from the freeway.
              </p>

              <ol className="mt-6 space-y-4">
                {[
                  {
                    step: '1',
                    title: 'Exit Leavesley Road',
                    body: 'Take Exit 360 for Leavesley Rd from US-101 (north or south).',
                  },
                  {
                    step: '2',
                    title: 'Head East on Leavesley Rd',
                    body: 'Follow Leavesley Rd east for about 0.5 miles.',
                  },
                  {
                    step: '3',
                    title: 'Turn onto Murray Ave',
                    body: 'Turn right (south) on Murray Ave, then a short block later turn onto Tomkins Court.',
                  },
                  {
                    step: '4',
                    title: 'Arrive at ACT',
                    body: '370 Tomkins Court — parking is available on-site. Look for the ACT flame.',
                  },
                ].map((s) => (
                  <li key={s.step} className="flex gap-4">
                    <div className="h-9 w-9 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center font-bold text-sm">
                      {s.step}
                    </div>
                    <div>
                      <div className="font-semibold text-[#0a1030]">{s.title}</div>
                      <div className="text-sm text-[#5a5148] mt-0.5 leading-relaxed">
                        {s.body}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>

              <Button
                asChild
                className="mt-8 rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white h-12 px-8"
              >
                <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer">
                  <Car className="h-4 w-4 mr-2" /> Open in Google Maps
                </a>
              </Button>

              <p className="mt-4 text-xs text-[#8a8478]">
                Note: exact turns may change with construction — Google Maps has
                the freshest route.
              </p>
            </div>

            <div className="lg:col-span-3">
              <div className="rounded-2xl overflow-hidden border border-black/5 shadow-md bg-white aspect-[4/3] lg:aspect-auto lg:h-[560px]">
                <iframe
                  title="ACT Gilroy location"
                  src={MAP_EMBED_URL}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default Contact;
